/**
 * @file GatewaysPage.tsx
 * @description US5 — Gateway Inventory page with KPI cards, datasource detail, CSV export
 * @feature 142-governance-compliance-dashboard
 * @constitution v1.12.0
 * Conformed to Spec 197 design blueprint
 */

import React, { useState } from 'react';
import { Server, Layers, AlertTriangle, Download, Database, Eye } from 'lucide-react';
import { useGateways, useGatewayDatasources, downloadCsv } from '../../../services/operations/governanceService';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Badge,
  StatCard,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  AppTooltip,
  Extended,
  QuickGlance,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import type { Gateway } from '../../../types/governance';

// ============================================================================
// Datasource detail (opened from a row action)
// ============================================================================

const GatewayDatasourcesModal: React.FC<{
  gateway: Gateway | null;
  onClose: () => void;
}> = ({ gateway, onClose }) => {
  const { data: datasources, isLoading } = useGatewayDatasources(gateway?.id ?? null);

  return (
    <Modal
      isOpen={gateway != null}
      onClose={onClose}
      title={gateway ? `Datasources — ${gateway.gatewayName}` : 'Datasources'}
      size="lg"
    >
      {isLoading ? (
        <LoadingState rows={4} />
      ) : !datasources || datasources.length === 0 ? (
        <EmptyState
          icon={<Database className="h-10 w-10" />}
          title="No datasources connected"
          message="This gateway has no datasources."
        />
      ) : (
        <DataTable
          columns={[
            { key: 'datasourceName', header: 'Datasource Name', width: '40%', render: (d) => d.datasourceName || '—' },
            { key: 'datasourceType', header: 'Type', width: '30%', render: (d) => d.datasourceType || '—' },
            { key: 'credentialType', header: 'Credential', width: '30%', render: (d) => d.credentialType || '—' },
          ]}
          rows={datasources}
          rowKey={(d) => d.datasourceId}
          emptyMessage="No datasources connected"
          minWidth="44rem"
        />
      )}
    </Modal>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const GatewaysPage: React.FC = () => {
  const { data, isLoading, error, refetch } = useGateways();
  const [detailGateway, setDetailGateway] = useState<Gateway | null>(null);

  const handleExportCsv = () => {
    if (!data?.gateways) return;
    const date = new Date().toISOString().split('T')[0];
    let csv = 'Gateway Name,Type,Version,Status,Datasource Count\n';
    data.gateways.forEach((g) => {
      csv += `"${g.gatewayName}","${g.gatewayType || ''}","${g.version || ''}","${g.status || ''}","${g.datasourceCount}"\n`;
    });
    downloadCsv(csv, `gateways-${date}.csv`);
  };

  const isEmpty = !isLoading && (!data?.gateways || data.gateways.length === 0);
  const summary = data?.summary;

  const columns: DataTableColumn<Gateway>[] = [
    {
      key: 'gatewayName',
      header: 'Gateway Name',
      width: '34%',
      render: (g) => <span className="font-medium text-[var(--color-text-primary)]">{g.gatewayName}</span>,
    },
    {
      key: 'gatewayType',
      header: 'Type',
      width: '22%',
      render: (g) => g.gatewayType || <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '18%',
      render: (g) =>
        g.status ? (
          <Badge variant={g.status.toLowerCase() === 'live' ? 'success' : 'secondary'} isOutlined>
            {g.status}
          </Badge>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
    {
      key: 'datasourceCount',
      header: 'Datasources',
      width: '16%',
      render: (g) => (
        <Badge variant={g.isHighCapacity ? 'warning' : 'secondary'} isOutlined leftIcon={g.isHighCapacity ? <AlertTriangle className="h-3 w-3" /> : undefined}>
          {g.datasourceCount}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: '10%',
      render: (g) => (
        <AppTooltip title="View datasources">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setDetailGateway(g);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
        </AppTooltip>
      ),
    },
  ];

  return (
    <PageLayout
      title="Gateways"
      description="On-premises data gateway inventory"
      icon={<Server className="h-6 w-6" />}
            helpId="governance.gateways"
            onRefresh={() => { void refetch(); }}
            isRefreshing={isLoading}
            actions={
        <>
          <Button leftIcon={<Download className="h-4 w-4" />} onClick={handleExportCsv} disabled={isEmpty}>
            Export CSV
          </Button>
        </>
      }
    >
      {error ? (
        <ErrorState
          title="Failed to load gateways"
          message={error instanceof Error ? error.message : 'Unknown error'}
          onRetry={() => refetch()}
        />
      ) : (
        <>
          {/* Summary tiles */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Total Gateways" value={isEmpty ? '—' : summary?.totalGateways ?? '—'} icon={<Server className="h-4 w-4" />} />
            <StatCard label="Total Datasources" value={isEmpty ? '—' : summary?.totalDatasources ?? '—'} icon={<Layers className="h-4 w-4" />} />
            <StatCard label="Gateway Types" value={isEmpty ? '—' : summary?.byType?.length ?? '—'} icon={<Server className="h-4 w-4" />} />
            <StatCard
              label="High Capacity (>20)"
              value={isEmpty ? '—' : summary?.highCapacityGateways ?? '—'}
              valueClassName={(summary?.highCapacityGateways ?? 0) > 0 ? 'text-[var(--color-status-warning)]' : undefined}
              icon={<AlertTriangle className="h-4 w-4" />}
            />
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for the full details.
            </Card>
          </QuickGlance>

          <Extended>
          {/* Type breakdown */}
          {summary?.byType && summary.byType.length > 0 && (
            <Card className="mb-6 p-4">
              <h3 className="mb-2 text-sm font-medium text-[var(--color-text-primary)]">Gateway Types</h3>
              <div className="flex flex-wrap gap-2">
                {summary.byType.map((t) => (
                  <Badge key={t.gatewayType} variant="secondary">
                    {t.gatewayType}
                    <span className="ml-1 text-[var(--color-accent)]">({t.count})</span>
                  </Badge>
                ))}
              </div>
            </Card>
          )}

          {/* Table */}
          <Card padding="none">
            {isLoading ? (
              <div className="p-4">
                <LoadingState rows={6} />
              </div>
            ) : isEmpty ? (
              // Spec 286 T159 item 7: Fabric's List Gateways returns "all gateways the user has permission for"
              // (learn.microsoft.com/rest/api/fabric/core/gateways/list-gateways); an empty list is not an API switch.
              <EmptyState
                icon={<Database className="h-12 w-12" />}
                title="No gateways collected"
                message="Fabric lists only the gateways the collection worker's identity has a role on, so gateways it has no role on do not appear here. To collect a gateway, give the worker's identity a role on it in Fabric (Settings, Manage connections and gateways)."
              />
            ) : (
              <DataTable
                columns={columns}
                rows={data?.gateways ?? []}
                rowKey={(g) => g.id}
                onRowClick={(g) => setDetailGateway(g)}
                emptyMessage="No gateways found"
                minWidth="44rem"
              />
            )}
          </Card>
          </Extended>
        </>
      )}

      <GatewayDatasourcesModal gateway={detailGateway} onClose={() => setDetailGateway(null)} />
    </PageLayout>
  );
};

export default GatewaysPage;
